import { Role } from "@/features/auth/authContext";
import { ROLE } from "@/utils/constant";
import { MenuConfig } from "alurkerja-ui";
import {
  ChartColumn,
  ClipboardCheck,
  Earth,
  LucideBuilding,
  LucideBuilding2,
  LucideHospital,
  LucideHouse,
  LucideList,
  LucideMap,
  LucideNetwork,
  LucideParentheses,
} from "lucide-react";
import { createElement } from "react";
import {
  navE2EAPD,
  navE2EFunnel,
  navE2EPermit,
  navE2EWOManagement,
} from "./nav-config-e2e";
import { navRebuildConfig } from "./nav-config-rebuild";
import { navE2EPhase2Config } from "./nav-config-e2e-phase2";
import { navAbdLastmileFeeder } from "./nav-config-abd-backbone";

export interface SidebarMenuConfig extends Omit<MenuConfig, "label" | "child"> {
  allowedRoles?: Role[];
  child?: SidebarMenuConfig[];
  label: string | ((roles: Role[] | undefined) => string);
}

export const navConfig: SidebarMenuConfig[] = [
  {
    href: "#",
    label: "Lastmile",
    child: [
      ...navE2EPermit,
      ...navE2EAPD,
      {
        href: "#",
        label: "ABD",
        child: [
          {
            href: "/dashboard/nro",
            label: "Dashboard NRO",
            icon: createElement(ChartColumn, { width: 20, height: 20 }),
            allowedRoles: [
              ROLE.ADMINISTRATOR,
              ROLE.MONITORING_TEAM,
              ROLE.REVISE_DOCUMENT_OFFICER,
              ROLE.VALIDATOR_DOCUMENT_OFFICER,
              ROLE.VENDOR,
              ROLE.VENDOR_VIEW,
            ],
          },
          {
            href: "/dashboard/rebuild",
            label: "Dashboard Rebuild",
            icon: createElement(ChartColumn, { width: 20, height: 20 }),
            allowedRoles: [
              ROLE.ADMINISTRATOR,
              ROLE.MONITORING_TEAM,
              ROLE.REVISE_DOCUMENT_OFFICER,
              ROLE.VALIDATOR_DOCUMENT_OFFICER,
              ROLE.VENDOR,
              ROLE.VENDOR_VIEW,
            ],
          },
          {
            href: "/rfs/rfs",
            label: "RFS List",
            icon: createElement(Earth, { width: 20, height: 20 }),
            allowedRoles: [
              ROLE.ADMINISTRATOR,
              ROLE.MONITORING_TEAM,
              ROLE.REVISE_DOCUMENT_OFFICER,
              ROLE.VALIDATOR_DOCUMENT_OFFICER,
              ROLE.VENDOR,
              ROLE.FUNNEL_MANAGEMENT,
              ROLE.VENDOR_VIEW,
            ],
          },
        ],
      },
      ...navE2EFunnel,
      ...navAbdLastmileFeeder,
    ],
  },
  ...navE2EPhase2Config,
  ...navE2EWOManagement,
  {
    href: "/master-data",
    label: "Master Data",
    child: [
      {
        href: "/master-data/apd",
        label: "APD",
        icon: createElement(LucideList, { width: 20, height: 20 }),
        allowedRoles: [
          ROLE.ADMINISTRATOR,
          ROLE.REVISE_DOCUMENT_OFFICER,
          ROLE.VALIDATOR_DOCUMENT_OFFICER,
          ROLE.VENDOR,
          ROLE.FUNNEL_MANAGEMENT,
          ROLE.SYS_ADMIN,
          ROLE.SYS_ADMIN_VIEW,
          ROLE.VENDOR_VIEW,
        ],
      },
      {
        href: "/master-data/subdistrict",
        label: "Sub District (Kel/Desa)",
        icon: createElement(LucideHouse, { width: 20, height: 20 }),
        allowedRoles: [
          ROLE.ADMINISTRATOR,
          ROLE.REVISE_DOCUMENT_OFFICER,
          ROLE.VALIDATOR_DOCUMENT_OFFICER,
          ROLE.VENDOR,
          ROLE.SYS_ADMIN,
          ROLE.SYS_ADMIN_VIEW,
          ROLE.VENDOR_VIEW,
        ],
      },
      {
        href: "/master-data/district",
        label: "District (Kec)",
        icon: createElement(LucideBuilding, { width: 20, height: 20 }),
        allowedRoles: [
          ROLE.ADMINISTRATOR,
          ROLE.REVISE_DOCUMENT_OFFICER,
          ROLE.VALIDATOR_DOCUMENT_OFFICER,
          ROLE.SYS_ADMIN,
          ROLE.SYS_ADMIN_VIEW,
        ],
      },
      {
        href: "/master-data/city",
        label: "City (Kab/Kota)",
        icon: createElement(LucideBuilding2, { width: 20, height: 20 }),
        allowedRoles: [
          ROLE.ADMINISTRATOR,
          ROLE.REVISE_DOCUMENT_OFFICER,
          ROLE.VALIDATOR_DOCUMENT_OFFICER,
          ROLE.SYS_ADMIN,
          ROLE.SYS_ADMIN_VIEW,
        ],
      },
      {
        href: "/master-data/province",
        label: "Province",
        icon: createElement(LucideMap, { width: 20, height: 20 }),
        allowedRoles: [
          ROLE.ADMINISTRATOR,
          ROLE.REVISE_DOCUMENT_OFFICER,
          ROLE.VALIDATOR_DOCUMENT_OFFICER,
          ROLE.SYS_ADMIN,
          ROLE.SYS_ADMIN_VIEW,
        ],
      },
      {
        href: "/master-data/category-bizzpass",
        label: "Category BizzPass",
        icon: createElement(LucideHospital, { width: 20, height: 20 }),
        allowedRoles: [
          ROLE.ADMINISTRATOR,
          ROLE.REVISE_DOCUMENT_OFFICER,
          ROLE.VALIDATOR_DOCUMENT_OFFICER,
          ROLE.VENDOR,
          ROLE.SYS_ADMIN,
          ROLE.SYS_ADMIN_VIEW,
          ROLE.VENDOR_VIEW,
        ],
      },
      {
        href: "/master-data/organization",
        label: "Organization",
        icon: createElement(LucideNetwork, { width: 20, height: 20 }),
        allowedRoles: [
          ROLE.ADMINISTRATOR,
          ROLE.REVISE_DOCUMENT_OFFICER,
          ROLE.VALIDATOR_DOCUMENT_OFFICER,
          ROLE.FUNNEL_MANAGEMENT,
          ROLE.SYS_ADMIN,
          ROLE.SYS_ADMIN_VIEW,
        ],
      },
      {
        href: "/master-data/master-param",
        label: "Master Param",
        icon: createElement(LucideParentheses, { width: 20, height: 20 }),
        allowedRoles: [ROLE.ADMINISTRATOR, ROLE.SYS_ADMIN, ROLE.SYS_ADMIN_VIEW],
      },
      {
        href: "/master-data/master-matrix-approval",
        label: "Master Matrix Approval",
        icon: createElement(ClipboardCheck, { width: 20, height: 20 }),
        allowedRoles: [ROLE.ADMINISTRATOR, ROLE.SYS_ADMIN, ROLE.SYS_ADMIN_VIEW],
      },
    ],
  },
  ...navRebuildConfig,
];
